import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { CircularProgress, Box } from '@mui/material';
import { getStoredAccessToken, decodeJwt } from '../../utils/auth0Utils';

interface RequireRoleProps {
  children: React.ReactElement;
  redirectTo?: string;
}

/**
 * RequireRole component that only allows superadmin access
 */
const RequireRole: React.FC<RequireRoleProps> = ({
  children,
  redirectTo = '/unauthorized',
}) => {
  const { isAuthenticated, user } = useAuth0();
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [isSuperUser, setIsSuperUser] = useState(false);

  useEffect(() => {
    const checkUserRole = async () => {
      console.log('🔍 Starting superadmin check...');

      if (isAuthenticated && user) {
        try {
          // ✅ Check superuser flag only
          const accessToken = getStoredAccessToken();
          if (accessToken) {
            const decodedToken = decodeJwt(accessToken);
            console.log('🪪 Decoded token payload:', decodedToken);

            if (decodedToken?.is_superuser) {
              console.log('✅ User is SUPERADMIN (is_superuser=true)');
              setIsSuperUser(true);
            } else {
              console.log('❌ User is NOT superadmin');
              setIsSuperUser(false);
            }
          } else {
            console.log('⚠️ No access token found');
            setIsSuperUser(false);
          }
        } catch (error) {
          console.error('❌ Error checking user role:', error);
          setIsSuperUser(false);
        } finally {
          setIsCheckingRole(false);
          console.log('✅ Role check completed');
        }
      } else {
        console.log('⚠️ Not authenticated, skipping role check');
        setIsCheckingRole(false);
      }
    };

    checkUserRole();
  }, [isAuthenticated, user, redirectTo]);

  // If still loading, show a minimal loading indicator
  if (isCheckingRole) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  // If not authenticated, redirect to home
  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  // If user is not superadmin, redirect to unauthorized
  if (!isSuperUser) {
    return <Navigate to={redirectTo} />;
  }

  return children;
};

export default RequireRole;